import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, PasswordInput, Stack } from '@mantine/core';
import { IconArrowLeft, IconLink } from '@tabler/icons-react';
import { useAuth } from '@actschurch/ccms-sdk';
import { PageIntro } from '@/components/rides/PageIntro';
import { Callout, ErrorState, LoadingState } from '@/components/ui';
import { takePlanningCentreRedirect } from '@/lib/planningCentre';
import { useCopy } from '@/lib/settings';

type Phase = 'exchanging' | 'linkRequired' | 'redirecting' | 'failed';

/**
 * Planning Centre OAuth callback (mirrors the Resource Centre page). Exchanges code + state; on
 * success continues to where the member was heading. If an account with the same email already
 * exists, the member confirms its password once to link the two.
 */
export default function PlanningCentreCallbackPage() {
  const navigate = useNavigate();
  const { planningCentre } = useAuth();
  const { t } = useCopy();
  const ran = useRef(false);
  const [destination] = useState(() => takePlanningCentreRedirect());

  const [phase, setPhase] = useState<Phase>('exchanging');
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke: the code can only be exchanged once
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errParam = params.get('error');
    if (errParam) {
      setError(errParam === 'access_denied' ? 'You cancelled the Planning Centre sign-in.' : "Planning Centre couldn't sign you in. Please try again.");
      setPhase('failed');
      return;
    }
    if (!code || !state) {
      setError('This sign-in link is incomplete. Please start again.');
      setPhase('failed');
      return;
    }
    (async () => {
      try {
        const result = await planningCentre.callback(code, state);
        if (result.status === 'LoggedIn') {
          setPhase('redirecting');
          navigate(destination, { replace: true });
        } else {
          setLinkToken(result.linkToken);
          setLinkEmail(result.email);
          setPhase('linkRequired');
        }
      } catch {
        setError("We couldn't finish signing you in. Please try again.");
        setPhase('failed');
      }
    })();
  }, [planningCentre, navigate, destination]);

  const onConfirmLink = async (e: FormEvent) => {
    e.preventDefault();
    if (!linkToken) return;
    setConfirming(true);
    setError(null);
    try {
      await planningCentre.confirmLink(linkToken, password);
      navigate(destination, { replace: true });
    } catch {
      setError("That password doesn't match this account. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  if (phase === 'exchanging' || phase === 'redirecting') {
    return (
      <div className="rides-main">
        <div style={{ paddingTop: 48 }}>
          <LoadingState variant="spinner" label={t('pc.signing_in').replace(/…$/, '')} />
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="rides-main">
        <PageIntro title="We couldn't sign you in" />
        <ErrorState
          title="Sign-in didn't finish"
          message={error ?? undefined}
          secondaryAction={<Button fullWidth onClick={() => navigate('/auth/login', { replace: true })}>Back to sign in</Button>}
        />
      </div>
    );
  }

  return (
    <div className="rides-main">
      <PageIntro title="Link your accounts" lede={t('pc.link_intro', undefined, { email: linkEmail ?? 'this email' })} />
      <div className="rides-card" style={{ padding: 18 }}>
        <form onSubmit={onConfirmLink}>
          <Stack gap="md">
            {error && <Callout tone="bad" live>{error}</Callout>}
            <PasswordInput label="Your Acts Church password" required value={password} onChange={(e) => setPassword(e.currentTarget.value)} autoComplete="current-password" autoFocus error={!!error || undefined} />
            <Button type="submit" loading={confirming} leftSection={<IconLink size={20} />} fullWidth>Link and continue</Button>
            <Button variant="subtle" onClick={() => navigate('/auth/forgot-password')} fullWidth>Forgot your password?</Button>
            <Button variant="subtle" color="gray" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate('/auth/login', { replace: true })} fullWidth>Back to sign in</Button>
          </Stack>
        </form>
      </div>
    </div>
  );
}
